import { API, postJSON } from "../../api.js";
import { $, esc, fmtMoney } from "../../ui/format.js";
import { helpIcon } from "../../ui/help.js";
import { toast } from "../../ui/modals.js";
import { renderBriefingInto } from "./briefing.js";

// ---------------------------------------------------------------- M&A

async function renderMA(main, snap) {
  main.innerHTML = `
    <h2 class="panel-title">M&amp;A — Month ${snap.month} ${helpIcon("ma")}</h2>
    <p style="font-size:12.5px; color:var(--text-mid); margin-bottom:20px;">Run a real acquisition process — LOI, due diligence, financing, Board approval on large deals, negotiation, and a genuine multi-month integration — or acquire a smaller target outright below for a fast, no-frills close.</p>

    <h3 style="font-family:var(--font-display); font-size:17px; margin:0 0 12px;">Acquisition Pipeline</h3>
    <div class="commands">
      <button class="primary" id="btn-start-loi">Start an LOI</button>
    </div>
    <div id="ma-pipeline">Loading…</div>

    <h3 style="font-family:var(--font-display); font-size:17px; margin:28px 0 12px;">Quick Acquisitions — This Month's Market</h3>
    <div id="ma-targets">Loading…</div>
    <h3 style="font-family:var(--font-display); font-size:17px; margin:28px 0 12px;">Acquisition History</h3>
    <div id="ma-history">Loading…</div>
    <div id="ma-briefing" style="margin-top:32px;"></div>
  `;
  $("btn-start-loi").addEventListener("click", openStartLoiModal);
  renderMAPipeline();
  renderBriefingInto("ma-briefing", "/ma/report", [
    ["Acquisitions Made", (s) => s.acquisitions_made], ["Total Spent", (s) => fmtMoney(s.total_spent)],
    ["Revenue Added", (s) => fmtMoney(s.total_revenue_added) + "/yr"], ["Cash Available", (s) => fmtMoney(s.cash_available)],
  ]);
  const [targets, history] = await Promise.all([
    fetch(`${API}/ma-targets`).then((x) => x.json()),
    fetch(`${API}/acquisitions`).then((x) => x.json()),
  ]);
  const acquiredNames = new Set(history.map((a) => a.target_name));
  $("ma-targets").innerHTML = targets.map((t) => `
    <div class="deal-card">
      <div class="deal-card-top">
        <div class="deal-card-name">${esc(t.name)}</div>
        ${acquiredNames.has(t.name) ? `<span class="status-pill signed">Owned</span>` : `<span class="status-pill open">Available</span>`}
      </div>
      <div class="deal-meta">Price ${fmtMoney(t.price)} · Implied revenue ${fmtMoney(t.implied_annual_revenue)}/yr · ${t.implied_employees} employees · ${esc(t.strategic_fit)}</div>
      ${acquiredNames.has(t.name) ? "" : `<div class="deal-actions"><button class="primary" data-acquire="${esc(t.name)}">Acquire for ${fmtMoney(t.price)}</button></div>`}
    </div>
  `).join("");
  $("ma-targets").querySelectorAll("[data-acquire]").forEach((btn) => {
    btn.addEventListener("click", async () => {
      const res = await postJSON("/acquire", { company_name: btn.dataset.acquire });
      await window.refreshAll();
      toast(res.message);
    });
  });

  $("ma-history").innerHTML = history.length ? `
    <table class="ledger">
      <tr><th>Target</th><th>Price</th><th>Revenue Added</th><th>Employees Added</th><th>Month</th></tr>
      ${history.map((a) => `
        <tr>
          <td class="label">${esc(a.target_name)}</td>
          <td class="mono">${fmtMoney(a.price)}</td>
          <td class="mono">${fmtMoney(a.implied_annual_revenue)}/yr</td>
          <td class="mono">${a.implied_employees}</td>
          <td class="mono">M${a.acquired_month}</td>
        </tr>
      `).join("")}
    </table>
  ` : `<p style="color:var(--text-mid)">No acquisitions yet.</p>`;
}

// --- M&A Pipeline (LOI -> Due Diligence -> Financing -> Board Review -> Negotiation -> Closing -> Integrating) ---

const MA_STAGE_LABEL = {
  LOI: "Letter of Intent", DueDiligence: "Due Diligence", Financing: "Choosing Financing",
  BoardReview: "Awaiting Board Approval", Negotiation: "Negotiating", Closing: "Closing",
  Integrating: "Integrating", Integrated: "Integrated", Withdrawn: "Withdrawn",
  "Lost to Rival": "Lost to a Rival",
};

// What the player needs to know about rivals on a pipeline deal: who has bid
// (and what it added to the price), or who bought the target out from under them.
// Reads rivals_circling from /ma/pipeline; empty/absent => nothing is rendered,
// so a deal no rival has noticed looks exactly as it did before.
function _rivalsCirclingHtml(d) {
  const list = Array.isArray(d.rivals_circling) ? d.rivals_circling : [];
  if (!list.length) return "";
  const lost = list.find((r) => r.action === "snatch_target");
  if (lost) {
    return `<div class="rival-circling rival-circling-lost" style="font-size:11.5px; color:var(--oxblood); margin-bottom:8px;">
      <strong>${esc(lost.competitor_name)}</strong> bought ${esc(d.target_name)} out from under you — the deal is lost.</div>`;
  }
  const bids = list.filter((r) => r.action === "counter_bid");
  const added = bids.reduce((sum, r) => sum + Math.max(0, ((r.effects || {}).new_price || 0) - ((r.effects || {}).old_price || 0)), 0);
  const names = bids.map((r) => esc(r.competitor_name)).join(", ");
  return `<div class="rival-circling" style="font-size:11.5px; color:var(--brass); margin-bottom:8px;">
    Rivals circling: <strong>${names}</strong> ${bids.length === 1 ? "has" : "have"} entered the bidding${added > 0 ? `, adding ${fmtMoney(added)} to the price` : ""}.</div>`;
}

function openStartLoiModal() {
  const backdrop = document.createElement("div");
  backdrop.className = "modal-backdrop";
  backdrop.innerHTML = `<div class="modal"><h3>Start an LOI</h3><div id="loi-target-list" style="max-height:340px; overflow-y:auto; margin:12px 0;">Loading…</div><div class="modal-actions"><button id="modal-cancel">Cancel</button></div></div>`;
  document.body.appendChild(backdrop);
  backdrop.querySelector("#modal-cancel").addEventListener("click", () => backdrop.remove());
  backdrop.addEventListener("click", (e) => { if (e.target === backdrop) backdrop.remove(); });

  fetch(`${API}/ma-targets`).then((x) => x.json()).then((targets) => {
    backdrop.querySelector("#loi-target-list").innerHTML = targets.map((t) => `
      <div class="deal-card">
        <div class="deal-card-name">${esc(t.name)}</div>
        <div class="deal-meta">Asking ${fmtMoney(t.price)} · Implied revenue ${fmtMoney(t.implied_annual_revenue)}/yr · ${t.implied_employees} employees · ${esc(t.strategic_fit)}</div>
        <div class="deal-actions"><button class="primary" data-loi="${esc(t.name)}">Start LOI</button></div>
      </div>
    `).join("");
    backdrop.querySelectorAll("[data-loi]").forEach((btn) => {
      btn.addEventListener("click", async () => {
        const res = await postJSON("/ma/pipeline/start", { company_name: btn.dataset.loi });
        backdrop.remove();
        await window.refreshAll();
        renderMAPipeline();
        toast(res.message);
      });
    });
  });
}

async function renderMAPipeline() {
  const el = $("ma-pipeline");
  if (!el) return;
  const pipeline = await fetch(`${API}/ma/pipeline`).then((x) => x.json());
  if (!pipeline.length) {
    el.innerHTML = `<p style="color:var(--text-mid)">No acquisitions in progress. Start an LOI on a larger target to run the full process.</p>`;
    return;
  }
  el.innerHTML = pipeline.map((d) => {
    const isOpen = d.status === "open";
    const stageLabel = MA_STAGE_LABEL[d.stage] || d.stage;
    let actionsHtml = "";
    if (isOpen) {
      if (d.stage === "LOI") {
        actionsHtml = `<button class="primary" data-dd="${d.id}">Run Due Diligence</button><button data-withdraw-ma="${d.id}">Withdraw</button>`;
      } else if (d.stage === "Financing") {
        actionsHtml = `
          <button class="primary" data-financing="${d.id}" data-method="cash">Finance: Cash</button>
          <button data-financing="${d.id}" data-method="debt">Finance: Debt</button>
          <button data-financing="${d.id}" data-method="stock">Finance: Stock</button>
          <button data-financing="${d.id}" data-method="mixed">Finance: Mixed</button>
          <button data-withdraw-ma="${d.id}">Withdraw</button>`;
      } else if (d.stage === "BoardReview") {
        actionsHtml = `<button class="primary" data-board-approval="${d.id}">Request Board Approval</button><button data-withdraw-ma="${d.id}">Withdraw</button>`;
      } else if (d.stage === "Negotiation") {
        actionsHtml = `
          <button class="primary" data-close-ma="${d.id}">Close Acquisition</button>
          ${d.negotiation_count < 2 ? `
            <button data-negotiate-ma="${d.id}" data-tactic="aggressive" title="Bigger price cut (~8%) — but a real 15% chance it backfires and price ticks up instead. Costs the full advisory fee either way.">Push Hard</button>
            <button data-negotiate-ma="${d.id}" data-tactic="collaborative" title="Smaller guaranteed price cut (~4%) — zero risk of making things worse. Half the advisory fee.">Build Relationship</button>
          ` : ""}
          <button data-withdraw-ma="${d.id}">Withdraw</button>`;
      }
    }
    return `
      <div class="deal-card">
        <div class="deal-card-top">
          <div class="deal-card-name">${esc(d.target_name)}</div>
          <span class="status-pill ${isOpen ? "open" : (d.stage === "Integrated" ? "signed" : "rejected")}">${esc(stageLabel)}</span>
        </div>
        <div class="deal-meta">
          Asking ${fmtMoney(d.asking_price)} · Current ${fmtMoney(d.current_price)} · ${esc(d.strategic_fit)}
          ${d.board_approval_required ? ` · <span style="color:var(--brass);">Board approval required</span>` : ""}
          ${d.due_diligence_done ? ` · Risk ${Math.round(d.dd_risk_score)}/100${d.dd_hidden_liability ? ` · Hidden liability ${fmtMoney(d.dd_hidden_liability)}` : ""}` : ""}
          ${d.financing_method ? ` · Financing: ${esc(d.financing_method)}` : ""}
        </div>
        ${_rivalsCirclingHtml(d)}
        ${d.stage === "Integrating" ? `<div style="font-size:11.5px; color:var(--text-mid); margin-bottom:8px;">Integrating — month ${d.integration_months_elapsed} of ${d.integration_months_total}. Planned synergy ${fmtMoney(d.planned_synergy_annual || 0)}/yr, realized so far ${fmtMoney(d.realized_synergy_annual || 0)}/yr.</div>` : ""}
        ${d.stage === "Integrated" ? `<div style="font-size:11.5px; margin-bottom:8px;">Integration outcome: <strong>${esc(d.integration_outcome || "—")}</strong> · Realized synergy ${fmtMoney(d.realized_synergy_annual || 0)}/yr of ${fmtMoney(d.planned_synergy_annual || 0)}/yr planned.</div>` : ""}
        ${actionsHtml ? `<div class="deal-actions">${actionsHtml}</div>` : ""}
      </div>
    `;
  }).join("");

  el.querySelectorAll("[data-dd]").forEach((btn) => btn.addEventListener("click", async () => {
    const res = await postJSON("/ma/pipeline/due-diligence", { pipeline_id: parseInt(btn.dataset.dd) });
    renderMAPipeline(); toast(res.message);
  }));
  el.querySelectorAll("[data-financing]").forEach((btn) => btn.addEventListener("click", async () => {
    const res = await postJSON("/ma/pipeline/financing", { pipeline_id: parseInt(btn.dataset.financing), method: btn.dataset.method });
    renderMAPipeline(); toast(res.message);
  }));
  el.querySelectorAll("[data-board-approval]").forEach((btn) => btn.addEventListener("click", async () => {
    const res = await postJSON("/ma/pipeline/board-approval", { pipeline_id: parseInt(btn.dataset.boardApproval) });
    renderMAPipeline(); toast(res.message);
  }));
  el.querySelectorAll("[data-negotiate-ma]").forEach((btn) => btn.addEventListener("click", async () => {
    const res = await postJSON("/ma/pipeline/negotiate", { pipeline_id: parseInt(btn.dataset.negotiateMa), tactic: btn.dataset.tactic });
    renderMAPipeline(); toast(res.message);
  }));
  el.querySelectorAll("[data-close-ma]").forEach((btn) => btn.addEventListener("click", async () => {
    const res = await postJSON("/ma/pipeline/close", { pipeline_id: parseInt(btn.dataset.closeMa) });
    await window.refreshAll(); renderMAPipeline(); toast(res.message);
  }));
  el.querySelectorAll("[data-withdraw-ma]").forEach((btn) => btn.addEventListener("click", async () => {
    const res = await postJSON("/ma/pipeline/withdraw", { pipeline_id: parseInt(btn.dataset.withdrawMa) });
    renderMAPipeline(); toast(res.message);
  }));
}

// Late-bound: a later view may replace these (window.X = ...), so callers use window.X.
window.renderMA = renderMA;
